
import React, { useState } from 'react';
import { Mail, Lock, User as UserIcon, Loader2, Sparkles, Chrome, Github, ArrowRight, Facebook, Apple, ShieldCheck, ChevronLeft, CheckCircle2 } from 'lucide-react';
import { AppLanguage } from '../types';
import { translations } from '../services/translations';

interface AuthViewProps {
  language: AppLanguage;
  onAuth: (email: string, username: string) => void;
}

const AuthView: React.FC<AuthViewProps> = ({ language, onAuth }) => {
  const t = translations[language].auth;
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isForgotPassword) {
      if (!email) return;
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setResetSent(true);
        setTimeout(() => {
          setResetSent(false);
          setIsForgotPassword(false);
        }, 3000);
      }, 1500);
      return;
    }

    if (!email || !password || (!isLogin && !username)) return;
    setIsLoading(true);
    // Simulate authentication processing
    setTimeout(() => {
      setIsLoading(false);
      onAuth(email, username || email.split('@')[0]);
    }, 1200);
  };

  const handleSocialAuth = (provider: string) => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      onAuth(`${provider.toLowerCase()}@example.com`, `Zomi ${provider} User`);
    }, 1000);
  };

  return (
    <div className="relative flex-1 flex items-center justify-center p-6 bg-[#0d0d0d] overflow-y-auto custom-scrollbar">
      {/* Background Neural Effects */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-indigo-600/5 rounded-full blur-[120px] animate-pulse delay-700"></div>
      </div>

      <div className="w-full max-w-[440px] z-10 animate-fade-up py-10">
        
        {/* Top Branding / Badge */}
        <div className="flex flex-col items-center mb-10 text-center">
           <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center border border-white/10 shadow-2xl mb-6 group hover:scale-105 transition-transform duration-500">
             <Sparkles size={24} className="text-white" fill="currentColor" />
           </div>
           
           <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
             {isForgotPassword ? t.forgotTitle : (isLogin ? t.signInTitle : t.signUpTitle)}
           </h1>
           <p className="text-white/40 text-[15px] font-medium max-w-xs leading-relaxed">
             {isForgotPassword ? t.forgotSub : (isLogin ? t.signInSub : t.signUpSub)}
           </p>
        </div>

        {/* Tab Control (Only show if not in forgot password mode) */}
        {!isForgotPassword && (
          <div className="bg-[#171717] p-1.5 rounded-2xl flex items-center mb-8 border border-white/5">
             <button 
               onClick={() => setIsLogin(true)}
               className={`flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-all ${isLogin ? 'bg-white/10 text-white shadow-sm' : 'text-white/30 hover:text-white/60'}`}
             >
               {t.switchSignIn}
             </button>
             <button 
               onClick={() => setIsLogin(false)}
               className={`flex-1 py-2.5 text-[13px] font-semibold rounded-xl transition-all ${!isLogin ? 'bg-white/10 text-white shadow-sm' : 'text-white/30 hover:text-white/60'}`}
             >
               {t.switchSignUp}
             </button>
          </div>
        )}

        {/* Auth Card */}
        <div className="bg-[#121212] p-8 md:p-10 rounded-[2.5rem] border border-white/5 shadow-2xl">
          {resetSent ? (
            <div className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in slide-in-from-bottom-2">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 size={32} className="text-green-500" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{t.success}</h3>
              <p className="text-white/40 text-sm leading-relaxed">{t.resetSuccess}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {!isLogin && !isForgotPassword && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-white/40 ml-1">{t.username}</label>
                  <div className="relative">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                    <input
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter your name"
                      className="w-full bg-[#1a1a1a] border border-white/5 focus:border-blue-500/30 text-white rounded-2xl py-3.5 pl-12 pr-4 focus:outline-none transition-all placeholder:text-white/5 text-[15px] font-medium"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-xs font-semibold text-white/40 ml-1">{t.email}</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full bg-[#1a1a1a] border border-white/5 focus:border-blue-500/30 text-white rounded-2xl py-3.5 pl-12 pr-4 focus:outline-none transition-all placeholder:text-white/5 text-[15px] font-medium"
                  />
                </div>
              </div>

              {!isForgotPassword && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-semibold text-white/40 ml-1">{t.password}</label>
                    {isLogin && (
                      <button 
                        type="button"
                        onClick={() => setIsForgotPassword(true)}
                        className="text-[11px] font-bold text-blue-500/60 hover:text-blue-500 transition-colors"
                      >
                        {t.forgotLink}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-[#1a1a1a] border border-white/5 focus:border-blue-500/30 text-white rounded-2xl py-3.5 pl-12 pr-4 focus:outline-none transition-all placeholder:text-white/5 text-[15px] font-medium"
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-4 rounded-2xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-xl shadow-blue-900/20 active:scale-[0.98] text-[15px] mt-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Please wait
                    </>
                  ) : (
                    <>
                      {isForgotPassword ? t.resetBtn : t.signInBtn}
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                {isForgotPassword && (
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(false)}
                    className="w-full py-2 text-xs font-bold text-white/30 hover:text-white/60 transition-colors flex items-center justify-center gap-2"
                  >
                    <ChevronLeft size={14} />
                    {t.backToLogin}
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Social Logins - Only show if not in forgot password mode */}
          {!isForgotPassword && !resetSent && (
            <>
              <div className="relative my-10">
                 <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/5"></div>
                 </div>
                 <div className="relative flex justify-center text-[11px] font-semibold text-white/20 bg-[#121212] px-4 tracking-wide">
                    {t.or}
                 </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                 <button 
                    onClick={() => handleSocialAuth('Google')}
                    disabled={isLoading}
                    className="flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white border border-white/5 py-3 rounded-2xl transition-all text-sm font-semibold active:scale-[0.98] disabled:opacity-50"
                 >
                    <Chrome size={18} className="opacity-60" />
                    Google
                 </button>
                 <button 
                    onClick={() => handleSocialAuth('Facebook')}
                    disabled={isLoading}
                    className="flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white border border-white/5 py-3 rounded-2xl transition-all text-sm font-semibold active:scale-[0.98] disabled:opacity-50"
                 >
                    <Facebook size={18} className="text-blue-500 opacity-80" fill="currentColor" />
                    Facebook
                 </button>
                 <button 
                    onClick={() => handleSocialAuth('Github')}
                    disabled={isLoading}
                    className="flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white border border-white/5 py-3 rounded-2xl transition-all text-sm font-semibold active:scale-[0.98] disabled:opacity-50"
                 >
                    <Github size={18} className="opacity-60" />
                    GitHub
                 </button>
                 <button 
                    onClick={() => handleSocialAuth('Apple')}
                    disabled={isLoading}
                    className="flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 text-white border border-white/5 py-3 rounded-2xl transition-all text-sm font-semibold active:scale-[0.98] disabled:opacity-50"
                 >
                    <Apple size={18} fill="currentColor" className="opacity-80" />
                    Apple
                 </button>
              </div>
            </>
          )}
        </div>

        {/* Refined Footer */}
        <div className="mt-12 text-center">
           <p className="text-[10px] text-white/10 font-bold tracking-[0.4em] select-none uppercase">
             ZOMI TECH HUB • SECURE AUTH V2.5
           </p>
        </div>
      </div>
    </div>
  );
};

export default AuthView;
